import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import home from "../pro-home.module.css";
import { PRODUCT_VIDEO_TYPES, TALKING_TYPES, useOwnPictures } from "./home-pictures";
import { MakeTile, type MakeTileId, ToolTile } from "./make-tiles";
import { Recent } from "./Recent";
import classes from "./simple-sections.module.css";
import { VIDEO_TOOLS } from "./video-tools";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple/videos";

/** The prototype's "Make a video", in its order: its own flows, as Home's cards open them. */
const MAKE_VIDEO: readonly MakeTileId[] = ["ugc", "animate", "storyboard", "templates"];

function MakeVideo({ teamId, brandId }: { teamId: string; brandId: string }) {
  // Home's own reads (5 a page, the composers' keys), so the pictures cost no request here.
  const own: Partial<Record<MakeTileId, ReturnType<typeof useOwnPictures>>> = {
    ugc: useOwnPictures(teamId, brandId, TALKING_TYPES),
    animate: useOwnPictures(teamId, brandId, PRODUCT_VIDEO_TYPES),
  };
  return (
    <section className={classes.section} aria-labelledby="simple-make-video">
      <h2 id="simple-make-video" className={classes.sectionTitle}>
        Make a video
      </h2>
      <div className={classes.tiles}>
        {MAKE_VIDEO.map((id) => (
          <MakeTile key={id} id={id} teamId={teamId} brandId={brandId} own={own[id]} />
        ))}
      </div>
    </section>
  );
}

/**
 * The prototype's "Fix or change a video": a tool with the Studio's own form opens its page in
 * Simple (spec ui-perf/simple-video-tools); the two that run on the video composer are Coming soon.
 */
function FixVideo({ teamId, brandId }: { teamId: string; brandId: string }) {
  return (
    <section className={classes.section} aria-labelledby="simple-fix-video">
      <h2 id="simple-fix-video" className={classes.sectionTitle}>
        Fix or change a video
      </h2>
      <div className={classes.toolTiles}>
        {VIDEO_TOOLS.map((tool) => (
          <ToolTile key={tool.name} tool={tool} teamId={teamId} brandId={brandId} />
        ))}
      </div>
    </section>
  );
}

/**
 * Simple's Videos (spec ui-perf/simple-videos): the Created AI Simple prototype's `VIEWS.videos`,
 * its ways to make a video, its video tools, and the brand's recent videos.
 */
export function SimpleVideos() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  return (
    <div className={classes.page}>
      <h1 className={classes.title}>Videos</h1>
      <p className={classes.lead}>Make a new video, or fix one you already have.</p>
      <MakeVideo teamId={teamId} brandId={brandId} />
      <FixVideo teamId={teamId} brandId={brandId} />
      <Recent
        teamId={teamId}
        brandId={brandId}
        medium="video"
        title="Recent videos"
        empty="No videos yet. The ones the brand makes show up here."
        links={
          <>
            {/* The editor's projects are Pro's page; a project opens the editor, full window. */}
            <Link to="/teams/$teamId/brands/$brandId/pro/editor" params={{ teamId, brandId }} className={home.subtle}>
              Open Video Editor
            </Link>
            {/* Simple's Library on its Videos (spec ui-perf/simple-library). */}
            <Link
              to="/teams/$teamId/brands/$brandId/simple/library"
              params={{ teamId, brandId }}
              search={{ kind: "video" }}
              className={home.subtle}
            >
              All videos <ArrowRight size={14} aria-hidden />
            </Link>
          </>
        }
      />
    </div>
  );
}
